import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond,
  Eye, MessageSquare, Lock, Camera, FileCheck
} from 'lucide-react';

// Icon mapping
const IconMap = {
  Trophy, Award, Medal, Star, Crown, Flame, Zap, 
  Target, TrendingUp, Heart, ThumbsUp, CheckCircle,
  BadgeCheck, Sparkles, Rocket, Shield, Gem, Diamond,
  Camera, FileCheck
};

const requirementIcons = {
  five_star_reviews: Star,
  profile_views: Eye,
  total_reviews: MessageSquare,
  bookings: CheckCircle,
  photos_uploaded: Camera,
  certificates_uploaded: FileCheck
};

const AchievementsTab = () => {
  const [achievements, setAchievements] = useState([]);
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchAchievements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAchievements(data.achievements || []);
        setEarnedAchievements(data.earned_achievements || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  const renderIcon = (iconName, className = "w-5 h-5") => {
    const IconComponent = IconMap[iconName];
    return IconComponent ? <IconComponent className={className} /> : <Trophy className={className} />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading achievements...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="rounded-lg border bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-700">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 dark:bg-yellow-500/30 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{stats.five_star_reviews || 0}</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-500">5-Star Reviews</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-gradient-to-br from-blue-50 to-cyan-100 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-700">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg">
                <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.profile_views || 0}</p>
                <p className="text-xs text-blue-600 dark:text-blue-500">Profile Views</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 dark:bg-green-500/30 rounded-lg">
                <Trophy className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">{earnedAchievements.length}</p>
                <p className="text-xs text-green-600 dark:text-green-500">Badges Earned</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200 dark:border-purple-700">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 dark:bg-purple-500/30 rounded-lg">
                <Camera className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.photos_uploaded || 0}</p>
                <p className="text-xs text-purple-600 dark:text-purple-500">Photos</p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-teal-900/20 dark:to-cyan-900/20 border-teal-200 dark:border-teal-700">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/20 dark:bg-teal-500/30 rounded-lg">
                <FileCheck className="w-6 h-6 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-teal-700 dark:text-teal-400">{stats.certificates_uploaded || 0}</p>
                <p className="text-xs text-teal-600 dark:text-teal-500">Certificates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Your Achievements
        </h3>

        {achievements.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No achievements available yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {achievements.map((achievement) => {
              const isEarned = earnedAchievements.includes(achievement.key);
              const progress = achievement.progress || {};
              
              return (
                <Card 
                  key={achievement.achievement_id} 
                  className={`relative overflow-hidden transition-all ${
                    isEarned 
                      ? 'border-2 shadow-lg' 
                      : 'opacity-90 hover:opacity-100'
                  }`}
                  style={isEarned ? { borderColor: achievement.color } : {}}
                >
                  {/* Earned ribbon */}
                  {isEarned && (
                    <div 
                      className="absolute top-0 right-0 px-3 py-1 text-xs font-bold text-white"
                      style={{ backgroundColor: achievement.color }}
                    >
                      ✓ EARNED
                    </div>
                  )}

                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className={`p-3 rounded-xl ${isEarned ? 'shadow-md' : ''}`}
                        style={{ 
                          backgroundColor: isEarned ? `${achievement.color}30` : '#f3f4f6',
                          color: isEarned ? achievement.color : '#9ca3af'
                        }}
                      >
                        {renderIcon(achievement.icon, "w-8 h-8")}
                      </div>
                      <div>
                        <CardTitle className={`text-lg ${!isEarned && 'text-gray-600'}`}>
                          {achievement.name}
                        </CardTitle>
                        <p className="text-sm text-gray-500">{achievement.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 space-y-4">
                    {/* Requirements Progress */}
                    <div className="space-y-3">
                      {(progress.requirements || []).map((req, index) => {
                        const ReqIcon = requirementIcons[req.type] || CheckCircle;
                        return (
                          <div key={index} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <ReqIcon className={`w-4 h-4 ${req.is_complete ? 'text-green-500' : 'text-gray-400'}`} />
                                <span className={req.is_complete ? 'text-green-700' : 'text-gray-600'}>
                                  {req.description || req.type.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <span className={`font-medium ${req.is_complete ? 'text-green-600' : 'text-gray-500'}`}>
                                {req.current}/{req.target}
                              </span>
                            </div>
                            <Progress 
                              value={req.percentage} 
                              className="h-2"
                              style={{
                                '--progress-background': req.is_complete ? '#22c55e' : '#e5e7eb',
                                '--progress-foreground': req.is_complete ? '#22c55e' : achievement.color
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Overall Progress */}
                    <div className="pt-3 border-t flex items-center justify-between">
                      {isEarned ? (
                        <Badge 
                          className="text-white"
                          style={{ backgroundColor: achievement.color }}
                        >
                          {renderIcon(achievement.icon, "w-3 h-3 inline mr-1")}
                          {achievement.badge_text}
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Lock className="w-4 h-4" />
                          <span>{progress.completed_count}/{progress.total_count} requirements complete</span>
                        </div>
                      )}
                      <span className={`text-sm font-semibold ${isEarned ? 'text-green-600' : 'text-gray-500'}`}>
                        {progress.overall_percentage || 0}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="rounded-lg border bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-700">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-500/20 dark:bg-purple-500/30 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-purple-800 dark:text-purple-300">How to earn badges?</p>
              <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                Complete the requirements shown for each achievement. Your progress is tracked automatically. 
                When you meet all requirements, you'll earn the badge and it will be displayed on your profile!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementsTab;
